import { supabase } from "@/integrations/supabase/client";
import type { ListenerPhoneSettings } from "@/types/phone/listener";

interface TwilioPhoneNumber {
  id: string;
  phoneNumber: string;
  friendlyName: string;
  dateCreated: string;
  status: string;
  phoneType?: "local" | "tollFree";
}

interface AgentPhoneNumber {
  id: string;
  agent_id?: string;
  phone_number_id?: string;
  phone_number?: string;
  friendly_name?: string;
  date_created?: string;
  status?: string;
  country_code?: string;
  metadata?: Record<string, any>;
}

interface TwilioProvisionParams {
  phoneNumber?: string;
  areaCode?: string;
  friendlyName: string;
  agentId: string;
  countryCode?: string;
  isTestMode?: boolean;
}

interface ExistingNumberAttachmentParams {
  phoneNumber: string;
  friendlyName: string;
  agentId: string;
  source: "ported" | "own_twilio";
  countryCode?: string;
  businessName?: string;
  authorizedRepresentative?: string;
  contactEmail?: string;
  accountNumber?: string;
  currentCarrier?: string;
  billingAddress?: string;
  authorizationDocumentUrl?: string;
  latestBillUrl?: string;
  notes?: string;
}

interface CompleteExistingNumberAttachmentParams {
  phoneNumberRecordId?: number;
  phoneNumberId?: string;
  twilioIncomingPhoneNumberSid?: string;
}

interface MarkExistingNumberPortPendingParams {
  phoneNumberRecordId?: number;
  phoneNumberId?: string;
}

export const TwilioService = {
  async fetchAvailablePhoneNumbers(countryCode: string = "US", areaCode?: string): Promise<TwilioPhoneNumber[]> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "get-available-numbers",
          data: { countryCode, areaCode },
        },
      });

      if (error) throw new Error(error.message);
      return data.phoneNumbers || [];
    } catch (error: any) {
      console.error("Error fetching available phone numbers:", error);
      throw error;
    }
  },

  async provisionPhoneNumber(params: TwilioProvisionParams): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "provision-number",
          data: {
            phoneNumber: params.phoneNumber,
            areaCode: params.areaCode,
            friendlyName: params.friendlyName,
            agentId: params.agentId,
            countryCode: params.countryCode || "US",
            isTestMode: params.isTestMode || false,
          },
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error provisioning phone number:", error);
      throw error;
    }
  },

  async releasePhoneNumber(phoneNumberId: string, isTestMode: boolean = false): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "release-number",
          data: {
            phoneNumberId,
            isTestMode,
          },
        },
      });

      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error("Error releasing phone number:", error);
      throw error;
    }
  },

  async fetchAgentPhoneNumber(agentId: string): Promise<AgentPhoneNumber | null> {
    try {
      const { data, error } = await supabase.from("agent_phone_numbers").select("*").eq("agent_id", agentId).maybeSingle();

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error fetching agent phone number:", error);
      throw error;
    }
  },

  async deleteAgentPhoneNumber(agentId: string): Promise<void> {
    try {
      const { error } = await supabase.from("agent_phone_numbers").delete().eq("agent_id", agentId);

      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error("Error deleting agent phone number:", error);
      throw error;
    }
  },

  async fetchAgentPhoneNumbers(agentId: string): Promise<AgentPhoneNumber[]> {
    try {
      const { data, error } = await supabase.from("agent_phone_numbers").select("*").eq("agent_id", agentId);

      if (error) throw new Error(error.message);
      return data || [];
    } catch (error: any) {
      console.error("Error fetching agent phone numbers:", error);
      throw error;
    }
  },

  async updatePhoneNumberSettings(phoneNumberId: string, settings: any): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke("twilio-update-number", {
        body: { phoneNumberId, settings },
      });

      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error("Error updating phone number settings:", error);
      throw error;
    }
  },

  async getCallHistory(agentId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from("call_logs")
        .select("*")
        .eq("agent_id", agentId)
        .order("start_time", { ascending: false });

      if (error) throw new Error(error.message);
      return data || [];
    } catch (error: any) {
      console.error("Error fetching call history:", error);
      throw error;
    }
  },

  async updateCallStatus(callSid: string, status: string, duration?: number): Promise<void> {
    try {
      const updates: any = { status };
      if (duration) updates.duration = duration;
      if (status === "completed") updates.end_time = new Date().toISOString();

      const { error } = await supabase.from("call_logs").update(updates).eq("call_sid", callSid);

      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error("Error updating call status:", error);
      throw error;
    }
  },

  async provisionTransferNumber(params: {
    phoneNumber: string;
    friendlyName: string;
    description: string;
    agentId: string;
  }): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "transfer-number",
          data: {
            targetNumber: params.phoneNumber,
            friendlyName: params.friendlyName,
            description: params.description,
            agentId: params.agentId,
          },
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error provisioning transfer number:", error);
      throw error;
    }
  },

  async updateTransferNumber(params: {
    phoneNumberId: string;
    friendlyName?: string;
    description?: string;
  }): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "update-transfer-number",
          data: params,
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error updating transfer number:", error);
      throw error;
    }
  },

  async provisionEscalationNumber(params: {
    phoneNumber: string;
    friendlyName: string;
    description: string;
    agentId: string;
  }): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "provision-escalation-number",
          data: {
            targetNumber: params.phoneNumber,
            friendlyName: params.friendlyName,
            description: params.description,
            agentId: params.agentId,
          },
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error provisioning escalation number:", error);
      throw error;
    }
  },

  async updateEscalationNumber(params: {
    phoneNumberId: string;
    targetNumber?: string;
    friendlyName?: string;
    description?: string;
  }): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "update-escalation-number",
          data: params,
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error updating escalation number:", error);
      throw error;
    }
  },

  async releaseEscalationNumber(phoneNumberId: string): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "release-escalation-number",
          data: { phoneNumberId },
        },
      });

      if (error) throw new Error(error.message);
    } catch (error: any) {
      console.error("Error releasing escalation number:", error);
      throw error;
    }
  },

  async requestExistingNumberAttachment(params: ExistingNumberAttachmentParams): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "request-existing-number-attachment",
          data: params,
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error requesting existing number attachment:", error);
      throw error;
    }
  },

  async completeExistingNumberAttachment(params: CompleteExistingNumberAttachmentParams): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "complete-existing-number-attachment",
          data: params,
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error completing existing number attachment:", error);
      throw error;
    }
  },

  async markExistingNumberPortPending(params: MarkExistingNumberPortPendingParams): Promise<TwilioPhoneNumber> {
    try {
      const { data, error } = await supabase.functions.invoke("twilio-handler", {
        body: {
          action: "mark-existing-number-port-pending",
          data: params,
        },
      });

      if (error) throw new Error(error.message);
      return data;
    } catch (error: any) {
      console.error("Error marking existing number port pending:", error);
      throw error;
    }
  },

  async updateListenerPhoneSettings(params: {
    phoneNumberId: string;
    settings: Partial<ListenerPhoneSettings>;
  }): Promise<ListenerPhoneSettings> {
    const { data, error } = await supabase.functions.invoke("twilio-handler", {
      body: {
        action: "update-listener-phone-settings",
        data: params,
      },
    });

    if (error) {
      // The handler's validation messages are written for the genie owner — surface them.
      const response = (error as { context?: unknown }).context;
      const body = response instanceof Response ? await response.clone().json().catch(() => null) : null;
      throw new Error(typeof body?.error === "string" ? body.error : "Failed to save call routing");
    }
    return data.settings;
  },
};
